/**
 * Utilidad para desencriptar tokens RSA usando la misma clave privada que Unity
 * Compatible con RSADecryptionBouncyCastle.cs
 */

import forge from 'node-forge';

// Clave privada RSA (misma que Unity)
const PRIVATE_KEY_PEM = `-----BEGIN PRIVATE KEY-----
MIIEvgIBADANBgkqhkiG9w0BAQEFAASCBKgwggSkAgEAAoIBAQCwHrb0jeH5azF8
SKayjtVKdk2WPZiBHIRH0dl9SFURHiw1sNF/DqzMLLfB/Q53a30AYYnR3lmHRm7q
e4RlQdPvZHSWZbCXCbwny0mBt9XRgbYn7OhoZku89v9lPkBqCsGVeK710zVoVDZM
SgVR7hhRQP9D1zhmn+3xYzHp8K9e+j8ndOdLL9EKGgpUmxrBlY2z9mDY2Z8dom5Z
Cxb93rg+5WUGGU2I42GJAeC3OR393CIG0y3rGJOjflvsAYbdXWAnmFWZ+S8kfpHe
m50XsfdYoMAt/ZwRnEikfaMZo3199+2iiAQzE6F3D0NJEhdeTD6v6Mk8yhWT8mAB
35nxpIYFAgMBAAECggEAUQ4xwaezAqmU4tBE+BTIkTaVSroagAFx1+P6prAthm1O
h39TS5ijamyCPeOLTubHZu99EHu21I1T6tLiAgAj3B9tkPJILZsrX10t/WdwHrYF
8aYU6Ed8QAGlVSErYCKHKZWbYEPOZq+rNb0zaZuc0XiKgtyAdqrQRbITfMlqzNi3
ATsVPifUZZqQ+23aboNWFU4hRrH6DhvbIyaSBRx1LSjhccHusdM7knt8XdK8Cm8i
MrSdlGRB6OTHLi0XbUJd8ihyXXb4YuG/+pMMFR397QWsiwxo8qUbECh3atJz+y32
fRO/Ws26lx4C+RPPmDiQOUBLlX5PbretlyDCLKWKWQKBgQDBgjrmHYnsigqT74y6
2Uaf7RpxKaxEK3gDiAwMRDG8DlEDYi53Q7ROz/q6V5C5st+MpcnCsf9LfSDvuI7T
Ci+1Uv+jGehDwohEnxP83ri6rEALCqKFpzEslVbCxuO1j0lzH0vIulDigrk9xDR9
lX+8RsG0l5NGbX6Ve4Yrj6A3zwKBgQDo/uv3bJUMdtW1Y5bWfeCEsjLgliU3xeVI
0uq61/jYDaCqiuJuNy4cVUHB4zRnGdEsTh6m8xQItFrgMzVF9bC4l7zd1pOLGgmy
ZkIOhlP5/Kfr4FjuiE+YeagyB6t42VPVujt2+z0FoZPpYdbsgNcqe1hv1j0iDCGy
iEO54DHF6wKBgQC2MzPX9domwPciwbwFNo2cCXkLMgzEhxVSZefKKO+XPPeksu5D
V5lPV96hU8+uObDpUjS9wDE7186KSIEaTo4LMeIHpV4fZuw9iHcecmsHQSZv/wx1
wa24Z8iwTuqayhaDceU+U8fS7vkSIkq2MGx8TzcWHl4KQxCcGRmErJBT2wKBgG3F
9Sy8drjJoER2qFFkOwyp3HidpOlTDoSyAq+HI5G/UewnxSjCTLJCpNmRj1Xn/voN
0Dk+GzlCp/IrjjTo3ZsKuowWFaua1+i6bvLi7NvB4uep6pxvkni3ELyJiotEcVAo
Du4uzoBBsStq0lTZGwbD2FXQrTrGVnYD50zCyRCDAoGBALYWQ6uYzee5rpfmCZ/7
pZpSPsDSx/IgZ0fOyJ8nBJ07y0NceAim2Oufm8ueE26yBHfYYcb9w7WNP+xnQvnS
+CbJsklvxxQS2t7HbuK1FvtOfggpRPkzdXIfNWUPmyjVoKTOrXNqxZtrVofzy4EC
al8tmiQZ3h7045HzLJ1yzwxv
-----END PRIVATE KEY-----`;

/**
 * Desencripta un token RSA usando OAEP con SHA-256
 * Compatible con el método DecryptWithRSA de Unity
 * 
 * @param encryptedData - Token encriptado en Base64
 * @returns JSON desencriptado como string
 */
export function decryptWithRSA(encryptedData: string): string {
  try {
    // Convertir la clave privada PEM a un objeto de clave privada
    const privateKey = forge.pki.privateKeyFromPem(PRIVATE_KEY_PEM);

    // Decodificar los datos encriptados de base64
    const encryptedBytes = forge.util.decode64(encryptedData);

    // Desencriptar los datos usando RSA-OAEP con SHA-256
    const decrypted = privateKey.decrypt(encryptedBytes, 'RSA-OAEP', {
      md: forge.md.sha256.create(),
      mgf1: {
        md: forge.md.sha256.create(),
      },
    });

    // Convertir los bytes desencriptados a una cadena UTF-8
    return forge.util.decodeUtf8(decrypted);
  } catch (error) {
    // console.error('[RSA Decryption] Error al desencriptar token:', error);
    throw new Error(`Error al desencriptar token RSA: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}
